import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Plus, LogOut as LogOutIcon, AlertCircle, Crown } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Badge } from "@v2/components/ui/badge";
import { supabaseV2 } from "../../lib/supabase";
import { teamRoleLabel } from "../../lib/labels";
import type { TeamMemberRole } from "../../types";

type MembershipRow = {
  id: string;
  team_id: string;
  role: TeamMemberRole;
  team_name: string;
  team_slug: string;
};

export function MyTeamsCard({
  userId,
  onCreateNewTeam,
}: {
  userId: string;
  onCreateNewTeam?: () => void;
}) {
  const { t } = useTranslation("account");
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const membershipsQuery = useQuery({
    queryKey: ["my-memberships", userId],
    queryFn: async () => {
      const { data, error: qErr } = await supabaseV2
        .from("team_memberships")
        .select("id, team_id, role, teams(name, slug)")
        .eq("user_id", userId)
        .eq("status", "active");
      if (qErr) throw qErr;
      return ((data || []) as any[]).map((row): MembershipRow => ({
        id: row.id,
        team_id: row.team_id,
        role: row.role,
        team_name: row.teams?.name || "—",
        team_slug: row.teams?.slug || "",
      }));
    },
  });

  const leave = useMutation({
    mutationFn: async (teamId: string) => {
      const { error: rpcErr } = await supabaseV2.rpc("leave_team", { _team_id: teamId });
      if (rpcErr) throw rpcErr;
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["my-memberships"] });
      queryClient.invalidateQueries({ queryKey: ["memberships"] });
      queryClient.invalidateQueries({ queryKey: ["admin-teams-list"] });
    },
    onError: (e: any) => setError(e.message || String(e)),
  });

  const memberships = membershipsQuery.data || [];

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-start justify-between gap-2 flex-wrap">
        <div>
          <CardTitle className="text-lg">{t("myTeams.title")}</CardTitle>
          <CardDescription>
            {t("myTeams.description")}
          </CardDescription>
        </div>
        {onCreateNewTeam && (
          <Button size="sm" variant="outline" onClick={onCreateNewTeam} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            {t("myTeams.joinOrCreate")}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {membershipsQuery.isLoading && (
          <p className="text-xs text-muted-foreground">{t("myTeams.loading")}</p>
        )}
        {!membershipsQuery.isLoading && memberships.length === 0 && (
          <p className="text-xs text-muted-foreground">
            {t("myTeams.empty")}
          </p>
        )}
        {memberships.map((m) => (
          <div
            key={m.id}
            className="flex items-center justify-between gap-3 flex-wrap rounded-2xl bg-raised px-3 py-2"
          >
            <div className="min-w-0 flex items-center gap-2">
              {m.role === "founder" && <Crown className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />}
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{m.team_name}</p>
                <p className="text-[11px] text-muted-foreground font-mono truncate">{m.team_slug}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{teamRoleLabel(m.role) || m.role}</Badge>
              <Button
                size="sm"
                variant="ghost"
                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                disabled={leave.isPending}
                onClick={() => {
                  const ok = window.confirm(t("myTeams.leaveConfirm", { name: m.team_name }));
                  if (ok) leave.mutate(m.team_id);
                }}
              >
                <LogOutIcon className="h-3.5 w-3.5" />
                {leave.isPending ? t("myTeams.leaving") : t("myTeams.leave")}
              </Button>
            </div>
          </div>
        ))}
        {error && (
          <div className="flex items-start gap-2 text-xs text-rose-600 bg-rose-50 px-3 py-2 rounded-md">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
